import "core-js/modules/es.array.reverse.js";
var _class, _descriptor;
import "core-js/modules/web.dom-collections.iterator.js";
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import i18next from "i18next";
import { computed, makeObservable, observable, runInAction } from "mobx";
import Rectangle from "terriajs-cesium/Source/Core/Rectangle";
import AsyncLoader from "../Core/AsyncLoader";
import Result from "../Core/Result";
import CatalogMemberMixin, { getName } from "./CatalogMemberMixin";

// Unfortunately Cesium does not declare a single interface that represents a primitive,
// but here is what primitives have in common:

export let ImageryParts = (_class = class ImageryParts {
  static fromAsync(options) {
    const result = new ImageryParts({
      imageryProvider: undefined,
      alpha: options.alpha,
      clippingRectangle: options.clippingRectangle,
      show: options.show
    });
    options.imageryProviderPromise.then(imageryProvider => {
      if (imageryProvider) {
        runInAction(() => {
          result.imageryProvider = imageryProvider;
        });
      }
    });
    return result;
  }
  constructor(options) {
    var _options$alpha, _options$show;
    _initializerDefineProperty(this, "imageryProvider", _descriptor, this);
    _defineProperty(this, "alpha", 0.8);
    _defineProperty(this, "clippingRectangle", undefined);
    _defineProperty(this, "show", true);
    this.imageryProvider = options.imageryProvider;
    this.alpha = (_options$alpha = options.alpha) !== null && _options$alpha !== void 0 ? _options$alpha : 0.8;
    this.clippingRectangle = options.clippingRectangle;
    this.show = (_options$show = options.show) !== null && _options$show !== void 0 ? _options$show : true;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "imageryProvider", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return undefined;
  }
}), _class);

// This discriminator only discriminates between ImageryParts and DataSource
(function (_ImageryParts2) {
  function is(object) {
    return "imageryProvider" in object;
  }
  _ImageryParts2.is = is;
})(ImageryParts || (ImageryParts = {}));
export function isPrimitive(mapItem) {
  return "isDestroyed" in mapItem;
}
export function isCesium3DTileset(mapItem) {
  return "allTilesLoaded" in mapItem;
}
export function isTerrainProvider(mapItem) {
  return "hasVertexNormals" in mapItem;
}
export function isDataSource(object) {
  return "entities" in object;
}
function MappableMixin(Base) {
  var _class2;
  let MappableMixin = (_class2 = class MappableMixin extends Base {
    constructor() {
      super(...arguments);
      _defineProperty(this, "initialMessageShown", false);
      _defineProperty(this, "_mapItemsLoader", new AsyncLoader(this.forceLoadMapItems.bind(this)));
      makeObservable(this);
    }
    get isMappable() {
      return true;
    }
    get cesiumRectangle() {
      if (this.rectangle !== undefined && this.rectangle.east !== undefined && this.rectangle.west !== undefined && this.rectangle.north !== undefined && this.rectangle.south !== undefined) {
        return Rectangle.fromDegrees(this.rectangle.west, this.rectangle.south, this.rectangle.east, this.rectangle.north);
      }
      return undefined;
    }
    get shouldShowInitialMessage() {
      if (this.initialMessage !== undefined) {
        const hasTitle = this.initialMessage.title !== undefined && this.initialMessage.title !== "" && this.initialMessage.title !== null;
        const hasContent = this.initialMessage.content !== undefined && this.initialMessage.content !== "" && this.initialMessage.content !== null;
        return (hasTitle || hasContent) && !this.initialMessageShown;
      }
      return false;
    }
    get loadMapItemsResult() {
      return this._mapItemsLoader.result;
    }

    /**
     * Gets a value indicating whether map items are currently loading.
     */
    get isLoadingMapItems() {
      return this._mapItemsLoader.isLoading;
    }

    /**
     * Loads the map items. It is safe to call this as often as necessary.
     * This will also call `loadMetadata()`.
     * If the map items are already loaded or already loading, it will
     * return the existing promise.
     *
     * This returns a Result object, it will contain errors if they occur - they will not be thrown.
     * To throw errors, use `(await loadMetadata()).throwIfError()`
     *
     * {@see AsyncLoader}
     */
    async loadMapItems(force) {
      try {
        if (CatalogMemberMixin.isMixedInto(this)) (await this.loadMetadata()).throwIfError();
        (await this._mapItemsLoader.load(force)).throwIfError();
      } catch (e) {
        return Result.error(e, {
          message: "Failed to load `".concat(getName(this), "` mapItems"),
          importance: -1
        });
      }
      return Result.none();
    }

    /**
     * Forces load of the maps items. This method does _not_ need to consider
     * whether the map items are already loaded.
     *
     * It is guaranteed that `loadMetadata` has finished before this is called.
     *
     * You **can not** make changes to observables until **after** an asynchronous call {@see AsyncLoader}.
     *
     * Errors can be thrown here.
     *
     * {@see AsyncLoader}
     */

    /**
     * Array of MapItems to show on the map/chart when Catalog Member is shown
     */

    showInitialMessage() {
      // This function is deliberately not a computed,
      // this.terria.notificationState.addNotificationToQueue changes state
      this.initialMessageShown = true;
      return new Promise(resolve => {
        var _this$initialMessage$, _this$initialMessage$2;
        this.terria.notificationState.addNotificationToQueue({
          title: (_this$initialMessage$ = this.initialMessage.title) !== null && _this$initialMessage$ !== void 0 ? _this$initialMessage$ : i18next.t("notification.title"),
          width: this.initialMessage.width,
          height: this.initialMessage.height,
          confirmText: this.initialMessage.confirmation ? this.initialMessage.confirmText : undefined,
          message: (_this$initialMessage$2 = this.initialMessage.content) !== null && _this$initialMessage$2 !== void 0 ? _this$initialMessage$2 : "",
          key: "initialMessage:" + this.initialMessage.key,
          confirmAction: () => resolve(),
          showAsToast: this.initialMessage.showAsToast,
          toastVisibleDuration: this.initialMessage.toastVisibleDuration
        });

        // No need to wait for confirmation if the message is a toast
        if (this.initialMessage.showAsToast) {
          resolve();
        }
      });
    }
    dispose() {
      super.dispose();
      this._mapItemsLoader.dispose();
    }
  }, _applyDecoratedDescriptor(_class2.prototype, "cesiumRectangle", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "cesiumRectangle"), _class2.prototype), _class2);
  return MappableMixin;
}
(function (_MappableMixin2) {
  function isMixedInto(model) {
    return model && model.isMappable && "forceLoadMapItems" in model && typeof model.forceLoadMapItems === "function";
  }
  _MappableMixin2.isMixedInto = isMixedInto;
})(MappableMixin || (MappableMixin = {}));
export default MappableMixin;